import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DatepickerComponent } from '../../components/inputs/datepicker/datepicker.component';
import { addYears, format, parse } from 'date-fns';
import { BUTTON_STYLES, CONSTANTS } from '../../constants/constants';
import { Observable, Subscription } from 'rxjs';
import { CountriesHttp } from '../../services/countries.http';
import { Option } from '../../components/inputs/select/options';
import { AuthService, User } from '../../services/auth.service';
import { UsersService } from '../../services/users.service';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap/datepicker/ngb-date-struct';
import { UtilsService } from '../../services/utils.service';
import { TelInputComponent } from '../../components/inputs/tel-input/tel-input.component';
import { InnerNavigationOption } from '../../components/event-management/types';
import { MY_SETTINGS_SECTION } from './constants';

interface MySettingsForm {
  first_name: FormControl<string>;
  last_name: FormControl<string>;
  date_of_birth: FormControl<string>;
  country: FormControl<string | null>;
  phone_number: FormControl<string>;
  sex: FormControl<string>;
  avatar: FormControl<string | null>;
  email: FormControl<string>;
}

@Component({
  selector: 'app-my-settings',
  templateUrl: './my-settings.component.html',
  styleUrls: ['./my-settings.component.scss'],
})
export class MySettingsComponent implements OnInit {
  protected readonly BUTTON_STYLES = BUTTON_STYLES;

  public editMode: boolean = false;
  public showActionButton: boolean = false;
  public locationOptions: Option[] = [];
  public genderOptions: Option[] = CONSTANTS.GENDER_OPTIONS;
  public user$: Observable<User | null> = this.authService.user$;
  public isLoading: boolean = true;
  private subscriptions = new Subscription();
  private user: User | null = null;
  private saveInProgress: boolean = false;

  public activeMySettingsSection: string = MY_SETTINGS_SECTION.PERSONAL_DETAILS;

  public MY_SETTINGS_SECTIONS: InnerNavigationOption[] = [
    {
      id: MY_SETTINGS_SECTION.PERSONAL_DETAILS,
      title: 'my_settings_personal_details',
    },
  ];

  @ViewChild('telInput') telInput: TelInputComponent;

  public canChangeAvatar: boolean = false; // TODO: remove or set to true when avatar upload is possible

  get minDate(): NgbDateStruct {
    const minDate = addYears(new Date(), -140);

    return UtilsService.dateToNgbDateStruct(minDate);
  }

  get maxDate(): NgbDateStruct {
    return UtilsService.dateToNgbDateStruct(new Date());
  }

  @ViewChild('dp', { static: true })
  datepicker: DatepickerComponent | null = null;

  constructor(
    private countriesHttpService: CountriesHttp,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private usersService: UsersService
  ) {}

  public innerForm = new FormGroup<MySettingsForm>({
    first_name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    last_name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    date_of_birth: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    country: new FormControl(''),
    phone_number: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    sex: new FormControl('', {
      nonNullable: true,
    }),
    avatar: new FormControl(''),
    email: new FormControl('', {
      nonNullable: true,
    }),
  });

  ngOnInit(): void {
    this.subscriptions.add(
      this.countriesHttpService.getCountries().subscribe((countries) => {
        if (this.locationOptions.length === 0) {
          this.locationOptions = countries.map(
            ({ country_name, country_id }) => ({
              id: String(country_id),
              itemName: country_name,
            })
          );
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      })
    );

    this.user$.subscribe((user) => {
      if (!user) {
        return;
      }

      this.user = user;

      this.fillForm(user);
    });
  }

  fillForm(user: User | null): void {
    Object.entries(user ?? {}).forEach(([key, value]) => {
      if (key === 'dob') {
        key = 'date_of_birth';
        value = format(new Date(value), CONSTANTS.DATE_DISPLAY_FORMAT);
      }

      this.innerForm.get(key)?.setValue(value ?? '');
    });
  }

  get allowSave(): boolean {
    return Boolean(
      this.user?.id &&
        this.innerForm.valid &&
        this.innerForm.touched &&
        !this.saveInProgress
    );
  }

  submit(): void {
    if (!this.allowSave) {
      return;
    }

    const {
      date_of_birth,
      sex,
      first_name,
      last_name,
      country,
      phone_number: tmpPhoneNumber,
    } = this.innerForm.value;

    const tmpDateOfBirth = parse(
      date_of_birth ?? '',
      CONSTANTS.DATE_DISPLAY_FORMAT,
      new Date()
    );

    const dialCode = `+${
      this.telInput.iti?.getSelectedCountryData().dialCode ?? ''
    }`;
    const phone_number = `${dialCode}${tmpPhoneNumber?.replace(dialCode, '')}`;

    const userData: Partial<User> = {
      dob: format(tmpDateOfBirth, CONSTANTS.DATE_SQL_FORMAT),
      sex,
      first_name,
      last_name,
      country,
      phone_number,
    };

    this.saveInProgress = true;

    this.usersService.updateUser(this.user?.id ?? 0, userData).subscribe(() => {
      this.editMode = false;
      this.innerForm.markAsUntouched();
      this.cdr.detectChanges();
      this.authService.getUser(true);
      this.saveInProgress = false;

      setTimeout(() => {
        this.showActionButton = false;
        this.saveInProgress = false;
        this.cdr.detectChanges();
      }, 3000);
    });
  }

  onAllowEdit(): void {
    this.editMode = true;
    this.showActionButton = true;
  }

  onCancelEdit(): void {
    this.editMode = false;
    this.showActionButton = false;

    this.fillForm(this.user);
  }
}
